<script setup>
  import {inject, ref, watch} from "vue";

  import ComponentRowOriginFromMergeBadge from "@components/Badges/RowOriginFromMergeBadge.vue";
  import ComponentFakeInput from "@components/Inputs/FakeInput.vue";
  import ComponentFullDateInput from "@components/Inputs/FullDateInput.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import {MedButton, MedIcon, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "DiseasesTableRow",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    disease: {
      type: Object,
      required: true,
    },
    isDragging: {
      type: Boolean,
      required: true,
    },
  });

  const emit = defineEmits(["updateDiseases"]);

  const page = usePage();

  const deleteDialog = inject("recordEditDeleteRowDialog");
  const diseaseForm = ref(props.disease);

  watch(
    () => props.disease,
    () => {
      diseaseForm.value = props.disease;
    },
    {deep: true},
  );

  const changeSince = (newValue) => {
    diseaseForm.value.since = newValue;
    emit("updateDiseases");
  };

  const patchDisease = (overrides = {}) => {
    router.patch(
      route("diseases.update", {
        patient: page.props.patient.id,
        record: page.props.record.id,
        disease: diseaseForm.value.id,
      }),
      {
        abdata_key_miv: diseaseForm.value.abdata_key_miv,
        disease: diseaseForm.value.disease,
        since: diseaseForm.value.since,
        abdata_key_icdc: diseaseForm.value.abdata_key_icdc,
        name_icdc: diseaseForm.value.name_icdc,
        mc_disease_id: diseaseForm.value.mc_disease_id,
        row: diseaseForm.value.row,
        ...overrides,
      },
      {
        preserveScroll: true,
        only: ["flash", "errors", "diseases"],
      },
    );
  };

  const clearSince = () => {
    diseaseForm.value.since = null;
    patchDisease({since: null});
  };

  const showDeleteConfirmDialog = () => {
    deleteDialog({routeName: "diseases", payload: {disease: diseaseForm.value.id}});
  };
</script>

<template>
  <li class="grid grid-cols-12 gap-x-6 py-2" :data-test="getTestId('row')">
    <!-- row 0 - optional origin when merging records -->
    <ComponentRowOriginFromMergeBadge :hashid="diseaseForm.id" type="diseases" class="col-span-12 ml-8" />

    <!-- col 1 -  Erkrankung / ICD-10 / Allergie / Lebensumstand -->
    <div class="col-span-8 h-9 w-full items-center">
      <div class="flex items-center">
        <template v-if="isEditable">
          <slot name="prefix">
            <div class="more icon-wrap flex items-center justify-center">
              <MedTooltip side="left" content="Zeile verschieben" :disabled="isDragging">
                <span
                  role="button"
                  tabindex="0"
                  aria-label="Diagnose verschieben"
                  class="flex cursor-grab items-center active:cursor-grabbing"
                  drag-handle
                  @keydown.enter.prevent
                  @keydown.space.prevent
                >
                  <MedIcon size="lg" icon="drag_handle" />
                </span>
              </MedTooltip>
            </div>
          </slot>
        </template>

        <ComponentFakeInput
          :class="cn('ml-3 w-full')"
          :value="diseaseForm.disease ?? diseaseForm.name_icdc"
          :disabled="!isEditable"
          :test-id="getTestId('row-name')"
          :helper-text="
            !diseaseForm.abdata_key_miv && diseaseForm.abdata_key_icdc
              ? 'ICD-Code ' + diseaseForm.abdata_key_icdc + ' wird nicht in Checks berücksichtigt.'
              : null
          "
        />
      </div>
    </div>

    <!-- col 2 - Seit(Datum) -->
    <div class="col-span-3">
      <ComponentFullDateInput
        v-model="diseaseForm.since"
        :disabled="!isEditable"
        :test-id="getTestId('row-sinc')"
        @input="changeSince"
        @clear="clearSince"
      />
    </div>

    <div class="col-span-1 m-auto flex">
      <template v-if="isEditable">
        <MedTooltip content="Zeile löschen">
          <MedButton
            :aria-label="diseaseForm.disease ? `Diagnose ${diseaseForm.disease} löschen` : 'Diagnose löschen'"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('row-delete-button')"
            @click="showDeleteConfirmDialog"
          />
        </MedTooltip>
      </template>
    </div>
  </li>
</template>
